import { useCallback, useEffect, useState } from "react";
import { adminPost } from "@/lib/kbAdminClient";
import { DOC_TYPES } from "./types";

type CountMap = Partial<Record<string, number>>;

/**
 * Published library counts by `doc_type` (one admin search per type, capped) — lightweight coverage confidence.
 */
export function KbAdminCoverageStrip() {
  const [counts, setCounts] = useState<CountMap | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const next: CountMap = {};
      for (const dt of DOC_TYPES) {
        const r = await adminPost<{ results?: unknown[] }>("/api/kb/admin/action?action=search", {
          q: "",
          docType: dt,
          status: "published",
          limit: 100,
        });
        if (!r.ok) {
          setErr(r.error);
          setCounts(null);
          setLoading(false);
          return;
        }
        const row = r.data as { results?: unknown[] };
        const n = Array.isArray(row.results) ? row.results.length : 0;
        next[dt] = n;
      }
      setCounts(next);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (err) {
    return (
      <p className="text-[11px] text-amber-800 dark:text-amber-200/90 rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/80 dark:bg-amber-950/30 px-2 py-1.5">
        Coverage strip: {err}
      </p>
    );
  }

  if (loading && !counts) {
    return <p className="text-[11px] text-gray-500 dark:text-gray-400">Loading published coverage…</p>;
  }

  if (!counts) return null;

  const total = Object.values(counts).reduce<number>((sum, b) => sum + (b ?? 0), 0);

  return (
    <div className="rounded-xl border border-gray-200 dark:border-zinc-600 bg-white/90 dark:bg-zinc-900/50 px-3 py-2.5">
      <div className="text-[11px] font-semibold text-gray-800 dark:text-gray-100 mb-1.5">
        Published coverage by doc type ({total} articles total, per-type cap 100)
      </div>
      <div className="flex flex-wrap gap-1.5">
        {DOC_TYPES.map((dt) => (
          <span
            key={dt}
            className="inline-flex items-center gap-1 rounded-full border border-gray-200 dark:border-zinc-600 bg-gray-50 dark:bg-zinc-800/80 px-2 py-0.5 text-[10px] text-gray-800 dark:text-gray-200"
            title={dt}
          >
            <span className="font-mono text-[9px] opacity-80">{dt.replace(/_/g, " ")}</span>
            <span className="tabular-nums font-semibold">{counts[dt] ?? 0}</span>
          </span>
        ))}
      </div>
      <button
        type="button"
        onClick={() => void load()}
        disabled={loading}
        className="mt-2 text-[10px] font-medium text-indigo-700 hover:underline disabled:opacity-50 dark:text-indigo-300"
      >
        {loading ? "Refreshing…" : "Refresh counts"}
      </button>
    </div>
  );
}
